/**
 * Bulk trade pricing calculations.
 *
 * VERIFICATION vs fx-web (connectBulkTradeDetails.ts / utils.ts):
 * ---------------------------------------------------------------
 * This file reimplements the same calculation logic as fx-web but in a simpler, self-contained way.
 *
 * Key equivalences confirmed:
 *
 * - `convertPointsToRate(points, precision+1, precision-1)` (forex)
 *   ≡ `convertToRawWithPrecision(precision)(points)` (fx-web utils/margin.ts)
 *   Both compute: points / 10^(precision-1). forex adds a rounding step via `roundToFixed`.
 *
 * - `getImpactedDigitsFromPoints` (local, bottom of file)
 *   ≡ `getImpactedDigitsFromPoints` (fx-web state/fxBulks/selectors/utils.ts)
 *   Identical formula: abs > 0 ? Math.floor(Math.log10(1000 * abs)) : 0
 *
 * - `computeLegData` spot calculation (isSales=true)
 *   ≡ fx-web `makeSpotWithMargin` (utils.ts): spot ± convertToRaw(margin)
 *   Buy  → spot + convertPointsToRate(+spotMargin)
 *   Sell → spot + convertPointsToRate(-spotMargin)
 *
 * - `computeLegData` forwardPointsWithMargin
 *   ≡ fx-web `makeForwardPointWithMargin` (utils.ts)
 *   Buy  → forwardPts.Ask + forwardMarginPoints.Ask
 *   Sell → forwardPts.Bid - forwardMarginPoints.Bid
 *
 * - `computeLegData` allInWithMargin
 *   ≡ fx-web `makeAllIn` (utils.ts): spotWithMargin + convertToRaw(forwardPoints)
 *
 * SPLIT LEGS — direction sign correction ("marginDirectionSign"):
 * -----------------------------------------------------------------
 * In a bulk trade, a single date can have legs going in BOTH directions (e.g. Buy 1M and
 * Sell 0.5M on the same date). The backend nets them into a single "netted" view (Buy 0.5M)
 * and keeps the individual legs in the "split" view.
 *
 * The spot margin and commercial amount are defined from the NETTED perspective. For a split
 * leg whose direction is OPPOSITE to the netted leg (e.g. the Sell 0.5M when the net is Buy),
 * the margin must be shown with a FLIPPED sign:
 *   - "Spot com.margin PPS" (spotMargin): negative instead of positive
 *   - marginAmount: negative instead of positive
 *
 * This is what fx-web calls `wayFactor` (value: +1 or -1) in connectBulkTradeDetails.ts:
 *   `const wayFactor = nonNettedWay === nettedWay ? +1.0 : -1.0`
 * Here we use the more explicit name `marginDirectionSign` and the boolean parameter
 * `isOppositeDirectionSplitLeg` to make the intent obvious.
 *
 * fx-web also uses the NETTED quoteLeg's ForwardPoints for split leg "Points" calculation,
 * whereas this implementation uses the SPLIT quoteLeg's ForwardPoints. In practice the
 * backend sends identical rates for both views on the same date, so results match.
 */
import { assertIsDefined } from '@sgme/fp';

import type { BulkPricedCashNettedLeg, BulkTradeExecuted, BulkTradePriced } from '@/store/bulkTrade/state.ts';
import type { ISODate, Tenor } from '@/types/date.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import { convertPointsToRate } from '@/utils/number-point.ts';
import { roundToFixed } from '@/utils/number.ts';
import type { ReceivedBulkQuoteLeg } from '@/utils/rfs/type.ts';

export type LegData = {
  amount: number;
  paymentDate: ISODate;
  paymentDateTenor: Tenor;
  lineType: 'Spot' | 'Forward';
  way: 'Buy' | 'Sell';
  spotWithMargin: number;
  forwardPointsWithMargin: number;
  allInWithMargin: number;
  impactedDigitsSpot: number;
  impactedDigitsPoints: number;
  impactedDigitsAllIn: number;
  marginAmount: number;
  spotMargin: number;
};

export type Margin = {
  spotMargin: number;
  forwardMargin: number;
};

export function computeLegData(
  quoteLeg: ReceivedBulkQuoteLeg,
  nettedLegMargin: Margin,
  negociatedCurrency: CurrencyIndex,
  precision: number,
  isUserInternalSales: boolean,
  // Set to true when this is a split leg whose CustomerWay is OPPOSITE to the netted leg
  // for the same date. This flips the sign of spotMargin and marginAmount so they reflect
  // the correct accounting direction for that individual leg.
  // Equivalent to fx-web's `wayFactor = -1` (connectBulkTradeDetails.ts).
  isOppositeDirectionSplitLeg = false,
): LegData {
  // `way` from the client's perspective, accounting for negociated currency.
  // negociatedCurrency=1 → way follows CustomerWay directly.
  // negociatedCurrency=2 → way is flipped (CCY2 amount means the client perspective is reversed).
  // Equivalent to fx-web: `sl.getDisplayedSide(leg.way, amountCurrency)` combined with `sideToWay`.
  const way = negociatedCurrency === 1 ? quoteLeg.CustomerWay : quoteLeg.CustomerWay === 'Buy' ? 'Sell' : 'Buy';

  // SpotWithoutMargin: pick Bid or Ask based on CustomerWay.
  // Equivalent to fx-web: `getBidAskValueWithWay(spotWithoutMarginBidAsk)` in connectBulkTradeDetails.ts
  // where getBidAskValueWithWay uses quoteLeg.customerWay (Buy→Ask, Sell→Bid).
  const spotWithMarginWithoutMargin =
    quoteLeg.CustomerWay === 'Buy'
      ? Number(quoteLeg.SpotWithoutMargin?.Ask ?? 0)
      : Number(quoteLeg.SpotWithoutMargin?.Bid ?? 0);

  // spotWithMargin:
  // - Internal Sales: compute from spotWithoutMargin + margin (using convertPointsToRate).
  //   Equivalent to fx-web `makeSpotWithMargin(nettedWay, spotWithoutMargin, spotMarginPoints, precision)`
  //   (state/fxBulks/selectors/utils.ts): spot ± convertToRawWithPrecision(precision)(margin)
  //   where Ask (Buy) adds the margin, Bid (Sell) subtracts it.
  // - Non internal sales: use server-provided SpotWithMargin directly (same as fx-web).
  const spotWithMargin = isUserInternalSales
    ? roundToFixed(
        spotWithMarginWithoutMargin +
          convertPointsToRate(
            quoteLeg.CustomerWay === 'Buy' ? Number(nettedLegMargin.spotMargin) : -Number(nettedLegMargin.spotMargin),
            precision,
            precision - 1,
          ),
        precision + 1,
      )
    : quoteLeg.CustomerWay === 'Buy'
      ? Number(quoteLeg.SpotWithMargin.Ask)
      : Number(quoteLeg.SpotWithMargin.Bid);

  // forwardPointsWithMargin: forward points from the server + forward margin.
  // Equivalent to fx-web `makeForwardPointWithMargin(nettedWay, forwardPts, forwardMarginPoints)` (utils.ts):
  //   Ask (Buy)  → fwdPts + fwdMargin
  //   Bid (Sell) → fwdPts - fwdMargin
  // Note: fx-web reads forwardMarginPoints from the margin store; here it comes directly from
  // quoteLeg.ForwardMarginPoints, which holds the same server-provided value.
  const forwardPointsWithMargin =
    quoteLeg.CustomerWay === 'Buy'
      ? Number(quoteLeg.ForwardPoints.Ask) + Number(quoteLeg.ForwardMarginPoints?.Ask ?? 0)
      : Number(quoteLeg.ForwardPoints.Bid) - Number(quoteLeg.ForwardMarginPoints?.Bid ?? 0);

  // allInWithMargin: spot + forward points converted to rate.
  // Equivalent to fx-web `makeAllIn(spotWithMargin, fwdPoints, precision)` (utils.ts):
  //   adder(spotWithMargin, convertToRaw(fwdPoints))
  const allInWithMargin = roundToFixed(
    spotWithMargin + convertPointsToRate(forwardPointsWithMargin, precision + 1, precision - 1),
    precision + 1,
  );

  // impactedDigits: same formula as fx-web `getImpactedDigitsFromPoints` (state/fxBulks/selectors/utils.ts).
  // Used to highlight the digits visually impacted by the margin.
  // impactedDigits: same formula as fx-web `getImpactedDigitsFromPoints` (state/fxBulks/selectors/utils.ts).
  // Used to highlight the digits visually impacted by the margin.
  const impactedDigitsSpot = Math.max(0, getImpactedDigitsFromPoints(nettedLegMargin.spotMargin) - 1);
  const impactedDigitsPoints = getImpactedDigitsFromPoints(nettedLegMargin.forwardMargin);
  const impactedDigitsAllIn = Math.max(impactedDigitsSpot === 0 ? 0 : impactedDigitsSpot + 1, impactedDigitsPoints);

  // marginDirectionSign: +1 for normal legs, -1 for split legs whose direction is opposite
  // to the netted leg. Applied to both spotMargin and marginAmount so the sign reflects
  // the correct accounting direction.
  // Equivalent to fx-web's `wayFactor` (connectBulkTradeDetails.ts):
  //   `const wayFactor = nonNettedWay === nettedWay ? +1.0 : -1.0`
  const marginDirectionSign: 1 | -1 = isOppositeDirectionSplitLeg ? -1 : 1;

  const marginAmount =
    marginDirectionSign * getMarginAmount(quoteLeg, negociatedCurrency, nettedLegMargin, allInWithMargin, precision);

  return {
    lineType: quoteLeg.IsForward ? 'Forward' : 'Spot',
    way,

    amount: Number(quoteLeg.Amount),

    paymentDate: quoteLeg.Date,
    paymentDateTenor: quoteLeg.DateTenor,

    spotWithMargin,
    forwardPointsWithMargin,
    allInWithMargin,

    impactedDigitsSpot,
    impactedDigitsPoints,
    impactedDigitsAllIn,

    marginAmount,

    // spotMargin: the "Spot com.margin PPS" column value.
    // Equivalent to fx-web: `marginPoints = wayFactor * spotMarginPoints` (connectBulkTradeDetails.ts).
    // marginDirectionSign flips the sign for opposite-direction split legs.
    spotMargin: marginDirectionSign * nettedLegMargin.spotMargin + 0, // +0 avoids javascript -0 (see SGEFX-2705 in fx-web)
  };
}

export function computeAllSplittedLegsData(
  bulkTrade: BulkTradePriced | BulkTradeExecuted,
  precision: number,
  isUserInternalSales: boolean,
): LegData[] {
  const nettedLegByDate = getNettedLegByDate(bulkTrade.nettedLegs);

  return bulkTrade.splitLegs.map((splitLeg) => {
    const quoteLeg = splitLeg.quoteLeg;

    const nettedLeg = nettedLegByDate[splitLeg.quoteLeg.Date];
    assertIsDefined(nettedLeg, `no netted leg found for split leg date ${splitLeg.quoteLeg.Date}`);

    const nettedMargin = {
      spotMargin: nettedLeg.spotCommercialMarginPPS,

      forwardMargin:
        splitLeg.quoteLeg.CustomerWay === 'Buy'
          ? Number(splitLeg.quoteLeg.ForwardMarginPoints?.Ask ?? 0)
          : Number(splitLeg.quoteLeg.ForwardMarginPoints?.Bid ?? 0),
    };

    // Detect whether this split leg runs in the OPPOSITE direction vs the netted leg.
    // e.g. the netted result is Buy, but this individual split leg is a Sell.
    // In such cases, spotMargin and marginAmount must be displayed with a flipped sign.
    const isOppositeDirectionSplitLeg = splitLeg.quoteLeg.CustomerWay !== nettedLeg.quoteLeg.CustomerWay;

    return computeLegData(
      quoteLeg,
      nettedMargin,
      bulkTrade.negotiatedCurrencyIndex,
      precision,
      isUserInternalSales,
      isOppositeDirectionSplitLeg,
    );
  });
}

export function getNettedLegByDate(allNettedLegs: BulkPricedCashNettedLeg[]): Record<ISODate, BulkPricedCashNettedLeg> {
  return allNettedLegs.reduce(
    (legByDate, leg) => {
      legByDate[leg.quoteLeg.Date] = leg;

      return legByDate;
    },
    {} as Record<ISODate, BulkPricedCashNettedLeg>,
  );
}

function getImpactedDigitsFromPoints(points: number) {
  const abs = Math.abs(points);
  return abs > 0 ? Math.floor(Math.log10(1000 * abs)) : 0;
}

function getMarginAmount(
  quoteLeg: ReceivedBulkQuoteLeg,
  amountCurrency: CurrencyIndex,
  margin: Margin,
  allInWithMargin: number,
  precision: number,
): number {
  const way = quoteLeg.CustomerWay;
  const amount = Number(quoteLeg.Amount);

  if (amountCurrency === 1) {
    return amount * convertPointsToRate(margin.spotMargin + margin.forwardMargin, precision + 1, precision - 1);
  }

  const totalMarginRate = convertPointsToRate(margin.spotMargin + margin.forwardMargin, precision + 1, precision - 1);

  if (way === 'Sell') {
    return Math.round(amount * (1 / (allInWithMargin - totalMarginRate) - 1 / allInWithMargin));
  }

  return Math.round(amount * (1 / allInWithMargin - 1 / (allInWithMargin + totalMarginRate)));
}
